import {
  makeStyles, tokens,
  Body1, Button,
  Dialog, DialogTrigger, DialogSurface,
} from '@fluentui/react-components';
import { InfoRegular, DismissRegular } from '@fluentui/react-icons';
import { brandColors, brandFonts } from '../../theme/brandColors';

/* Shared metric explainer dialog — the Transformr dialog standard (info variant).
   Reference: dialog-standard.context.md (locked 2026-06-07). Extracted here (per the
   standard's "extract on second use" note) so every report page's metric explanations
   render identically. An (i) icon button (light gray on the navy hero) opens a dialog
   with the metric name as the title, a top-right close (X), and the description in the body.
   No list/search/rows (info dialog), so those standard keys are intentionally omitted;
   BusyOverlay / error MessageBar are N/A (no mutation, no error). */

const useStyles = makeStyles({
  // Light-gray (i) button on the navy hero; brightens to white on hover/focus.
  infoIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0,
    padding: 0, border: 'none', backgroundColor: 'transparent',
    color: '#C8C8C8', cursor: 'pointer',
    '&:hover': { color: brandColors.white },
    '&:focus-visible': { outline: `2px solid ${brandColors.lightBlue}`, outlineOffset: '2px', borderRadius: tokens.borderRadiusSmall },
  },
  dlgSurface: { padding: 0, maxWidth: '34rem', width: '100%', overflow: 'hidden' },
  dlgShell: { display: 'flex', flexDirection: 'column', maxHeight: '85vh' },
  dlgHead: {
    position: 'relative',
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL} ${tokens.spacingVerticalL}`,
    backgroundColor: brandColors.white,
    borderBottom: `1px solid ${brandColors.greyLine}`,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, flexShrink: 0,
  },
  dlgKicker: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.18em', textTransform: 'uppercase', color: brandColors.greyText,
  },
  dlgKickerRule: {
    display: 'inline-block', width: '2rem', height: '2px',
    backgroundColor: brandColors.blue, borderRadius: '1px', flexShrink: 0,
  },
  dlgTitle: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, letterSpacing: '-0.02em', margin: 0,
    paddingRight: tokens.spacingHorizontalXXL, // clear the close X
  },
  dlgClose: {
    position: 'absolute', top: tokens.spacingVerticalL, right: tokens.spacingHorizontalL,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '2rem', height: '2rem', padding: 0, border: 'none', backgroundColor: 'transparent',
    borderRadius: tokens.borderRadiusMedium, cursor: 'pointer', color: brandColors.greyText,
    '&:hover': { color: brandColors.navy, backgroundColor: tokens.colorNeutralBackground1Hover },
    '&:focus-visible': { outline: `2px solid ${brandColors.blue}`, outlineOffset: '1px' },
  },
  dlgBody: {
    flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, backgroundColor: brandColors.white,
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL,
  },
  dlgInfoText: { color: tokens.colorNeutralForeground1, lineHeight: tokens.lineHeightBase400 },
  dlgFoot: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: tokens.spacingHorizontalL,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    borderTop: `1px solid ${brandColors.greyLine}`, backgroundColor: brandColors.white, flexShrink: 0,
  },
});

export interface MetricInfoDialogProps {
  /** Metric name — used as the dialog heading and the icon's accessible label. Title Case. */
  title: string;
  /** Plain-English explanation shown in the dialog body. */
  content: string;
}

export function MetricInfoDialog({ title, content }: MetricInfoDialogProps) {
  const styles = useStyles();
  return (
    <Dialog>
      <DialogTrigger disableButtonEnhancement>
        <button type="button" className={styles.infoIcon} aria-label={`What does "${title}" mean?`}>
          <InfoRegular fontSize={16} aria-hidden />
        </button>
      </DialogTrigger>
      <DialogSurface className={styles.dlgSurface}>
        <div className={styles.dlgShell}>
          <div className={styles.dlgHead}>
            <span className={styles.dlgKicker}>
              <span className={styles.dlgKickerRule} aria-hidden /> About This Metric
            </span>
            <h2 className={styles.dlgTitle}>{title}</h2>
            <DialogTrigger action="close" disableButtonEnhancement>
              <button type="button" className={styles.dlgClose} aria-label="Close">
                <DismissRegular fontSize={20} />
              </button>
            </DialogTrigger>
          </div>
          <div className={styles.dlgBody}>
            <Body1 className={styles.dlgInfoText}>{content}</Body1>
          </div>
          <div className={styles.dlgFoot}>
            <DialogTrigger action="close" disableButtonEnhancement>
              <Button appearance="primary">Got It</Button>
            </DialogTrigger>
          </div>
        </div>
      </DialogSurface>
    </Dialog>
  );
}
